import { notification } from "antd";
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";

import { getConfigManifest } from "@/api/configManifest/api";
import { APP_VERSION, APP_VERSION_PERSIST, ENVIRONMENT } from "@/config";
import {
    applyUpdate,
    checkUpdate as checkUpdateSW,
    setOnNeedRefresh,
} from "@/Services/SW";
import { Maintenance } from "@/types";

const INTERVAL = 1 * 60 * 1000;

interface SWContextType {
    needRefresh: boolean;
    checkLoading: boolean;
    storeReset: boolean;
    applyUpdate: () => void;
    checkUpdate: () => Promise<void>;
    silentCheckUpdate: () => Promise<void>;
    maintenance: Maintenance | null;
}

export const SWContext = createContext<SWContextType>({
    needRefresh: false,
    applyUpdate: () => {},
    checkUpdate: () => Promise.resolve(),
    silentCheckUpdate: () => Promise.resolve(),
    checkLoading: false,
    storeReset: false,
    maintenance: null,
});

export const SWProvider = ({ children }: { children: ReactNode }) => {
    const [needRefresh, setNeedRefresh] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);
    const [storeReset, setStoreReset] = useState(false);
    const [maintenance, setMaintenance] = useState<null | Maintenance>(null);

    const checkUpdate = useCallback(async () => {
        try {
            setCheckLoading(true);
            const hasUpdate = await checkUpdateSW();

            if (!hasUpdate) {
                notification.info({
                    message: "Оновлень не знайдено",
                });

                setTimeout(() => window.location.reload(), 1000);
            }
        } catch (error) {
            notification.error({ message: "Не вдалося перевірити оновлення" });
            return;
        } finally {
            setCheckLoading(false);
        }
    }, []);

    const silentCheckUpdate = useCallback(async () => {
        try {
            await checkUpdateSW();
        } catch {
            return;
        }
    }, []);

    useEffect(() => {
        const unsubscribe = setOnNeedRefresh(() => setNeedRefresh(true));
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (ENVIRONMENT === "dev-local") return;

        const getConfig = async () => {
            try {
                const configManifest = await getConfigManifest();

                setMaintenance(configManifest.maintenance ?? null);

                const hasAppUpdate = APP_VERSION !== configManifest.version;
                const hasPersistUpdate =
                    APP_VERSION_PERSIST !== configManifest["persist-version"];

                setStoreReset(hasAppUpdate && hasPersistUpdate);

                if (hasAppUpdate) {
                    await silentCheckUpdate();
                }
            } catch (error) {
                console.error(error);
            }
        };

        getConfig();

        const intervalId = setInterval(getConfig, INTERVAL);

        return () => {
            clearInterval(intervalId);
        };
    }, [silentCheckUpdate]);

    return (
        <SWContext.Provider
            value={{
                needRefresh,
                applyUpdate,
                checkUpdate,
                silentCheckUpdate,
                checkLoading,
                storeReset,
                maintenance,
            }}
        >
            {children}
        </SWContext.Provider>
    );
};
